import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { maakWebshopOrderGratis } from '../../lib/orders';
import { formatCurrency } from '../../lib/format';

interface MaakWebshopGratisDialogProps {
  orderId: string;
  orderNumber: string;
  customerName: string;
  totalInclBtw: number;
  isOpen: boolean;
  onClose: () => void;
  /** Aangeroepen na succesvol gratis maken — caller herlaadt de order. */
  onSuccess: () => void;
}

/**
 * Bevestigingsdialoog om een doorberekende Webshop-order alsnog gratis te
 * maken. Zet elke regel op 100% korting (→ €0); wo-status-na-betaling flipt de
 * status zelf van 'Wachtend op betaling' naar 'Klaarmaken verzending'.
 */
export function MaakWebshopGratisDialog({
  orderId,
  orderNumber,
  customerName,
  totalInclBtw,
  isOpen,
  onClose,
  onSuccess,
}: MaakWebshopGratisDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await maakWebshopOrderGratis(orderId);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gratis maken mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Order gratis maken</h2>
          <p className="text-xs text-slate-500 mt-1">
            {orderNumber} · {customerName} · {formatCurrency(totalInclBtw)}
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-slate-700">
            Alle regels krijgen een 100% korting (→ <strong>€0</strong>). Het openstaande
            bedrag wordt nul en de order schuift door naar <strong>Klaarmaken verzending</strong> —
            de klant hoeft niets te betalen.
          </p>

          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Heeft de klant al een bevestiging met betaalverzoek ontvangen? Stuur dan even
            een korte mail dat de bestelling gratis is en de betaling kan vervallen.
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            )}
          >
            {submitting ? 'Bezig…' : 'Maak gratis'}
          </button>
        </div>
      </div>
    </div>
  );
}
